import { Close, Content, Description, Overlay, Portal, Root, Title } from '@radix-ui/react-dialog';
import { useLoadingFunction } from '@ui/hooks/useLoadingFunction';
import { wrapClassNames } from '@ui/utils/tailwind';
import type { ButtonHTMLAttributes, FC, MutableRefObject, PropsWithChildren } from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';

import type { ButtonProps, ButtonVariant } from '../v2/button';
import { Button } from '../v2/button';
import { Cross } from '../v2/icon';

export const ActionButton = forwardRef<
  HTMLButtonElement,
  PropsWithChildren<ButtonProps & ButtonHTMLAttributes<HTMLButtonElement>>
>(({ onClick, type = 'button', children, loader, variant, ...rest }, ref) => {
  const [clicker, isLoading] = useLoadingFunction(onClick);
  const [showLoader, setShowLoader] = useState(false);
  const [width, setWidth] = useState(0);

  const innerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setTimeout(() => {
      if (innerRef?.current?.getBoundingClientRect().width) {
        setWidth(innerRef?.current?.getBoundingClientRect().width);
      }
    }, 100);
  }, [children]);

  useEffect(() => {
    if (isLoading) {
      setShowLoader(true);
    }

    if (!isLoading && showLoader) {
      const timeout = setTimeout(() => {
        setShowLoader(false);
      }, 400);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isLoading, showLoader]);

  return (
    <Button
      {...rest}
      type={type}
      onClick={clicker}
      data-testid="action-button"
      ref={node => {
        (innerRef as MutableRefObject<HTMLButtonElement | null>).current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as MutableRefObject<HTMLButtonElement | null>).current = node;
        }
      }}
      variant={variant}
      loader={loader || showLoader}
      style={width ? { width: `${width}px` } : {}}
    >
      {children}
    </Button>
  );
});

export const overlayStyles = `inset-0 fixed bg-black/30 flex items-center justify-center z-[1075] [&[data-state='open']]:animate-dialog-overlay-in [&[data-state='closed']]:animate-dialog-overlay-out`;

export const contentAnimationStyles = `[&[data-state='open']]:animate-dialog-in [&[data-state='closed']]:animate-dialog-out motion-reduce:!animate-none`;

export { Close, Content, Description, Overlay, Portal, Root, Title };

interface DialogProps extends PropsWithChildren {
  variant?: ButtonVariant;
  onAction?: () => void;
  onClose: () => void;
  disableCloseOnClickOutside?: boolean;
  hideCancelButton?: boolean;
  open: boolean;
  title?: string;
  actionText: string;
  actionFormTarget?: string;
  actionLoading?: boolean;
  actionDisabled?: boolean;
  cancelText: string;
  desc?: string;
  contentClassName?: string;
}

export const Dialog: FC<DialogProps> = ({
  title = 'Confirm action',
  open,
  children,
  onClose,
  onAction,
  variant = 'default',
  contentClassName,
  actionFormTarget,
  actionLoading,
  actionDisabled,
  disableCloseOnClickOutside = false,
  hideCancelButton = false,
  desc,
  ...rest
}) => {
  const { cancelText, actionText } = rest;

  return (
    <Root open={open} onOpenChange={disableCloseOnClickOutside ? () => {} : onClose}>
      <Portal>
        <Overlay className={overlayStyles}>
          <Content
            className={wrapClassNames(
              `bottom-0 flex max-h-[90dvh] w-full flex-col gap-4 rounded-t-2xl bg-white p-4 text-left shadow-xl sm:max-w-[615px] sm:bottom-auto sm:max-h-[85dvh] sm:align-middle sm:rounded-b-2xl ${contentAnimationStyles}
              ${contentClassName}`
            )}
          >
            <div className="flex shrink-0 items-center">
              <Title asChild>
                <h3 className="text-lg font-medium leading-6 text-gray-700 grow">{title}</h3>
              </Title>

              <button
                onClick={e => {
                  e.preventDefault();
                  onClose();
                }}
                type="button"
                className="rounded-lg shrink-0 flex items-center justify-center bg-gray-100 w-8 h-8 transition ease-in-out duration-200 hocus:(outline-none bg-gray-200)"
              >
                <Cross className="w-3 h-3" />
              </button>
            </div>
            {desc && (
              <Description className="text-base text-center text-gray-700 w-full block shrink-0 sm:text-left">
                {desc}
              </Description>
            )}

            {children && (
              <div className="grow min-h-0 overflow-y-auto text-center text-sm sm:text-left">
                <div className="text-base text-gray-700">{children}</div>
              </div>
            )}

            <div className="flex shrink-0 rounded-b-2xl space-x-3 justify-center sm:justify-end">
              {!hideCancelButton && (
                <Button
                  onClick={e => {
                    e.preventDefault();
                    onClose();
                  }}
                  variant="secondary"
                >
                  {cancelText}
                </Button>
              )}
              {actionFormTarget && !onAction ? (
                <Button
                  variant={variant}
                  type="submit"
                  disabled={actionDisabled}
                  loader={actionLoading}
                  form={actionFormTarget}
                  className="whitespace-nowrap"
                >
                  {actionText}
                </Button>
              ) : null}
              {onAction ? (
                <ActionButton
                  variant={variant}
                  disabled={actionDisabled}
                  loader={actionLoading}
                  onClick={e => {
                    e.preventDefault();
                    onAction();
                  }}
                  type="button"
                  className="whitespace-nowrap"
                >
                  {actionText}
                </ActionButton>
              ) : null}
            </div>
          </Content>
        </Overlay>
      </Portal>
    </Root>
  );
};
